import { useLogto } from "@logto/react";
import Button, { type ButtonProps } from "@mui/material/Button";
import Link from "@mui/material/Link";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { SIGN_IN_CALLBACK_PATH } from "src/auth/dxmSsoConfig";
import { currentReturnTo, rememberReturnTo } from "src/auth/returnTo";
import { ROUTES } from "src/routes/paths";

interface SignInButtonProps {
	/** A button, or `link` for inline text ("Already have an account? Sign in"). */
	variant?: ButtonProps["variant"] | "link";
}

/** Starts the DXM SSO redirect. Only rendered when DXM SSO is configured (it needs the LogtoProvider). */
export const SignInButton: FC<SignInButtonProps> = ({ variant = "text" }) => {
	const { t } = useTranslation();
	const { signIn } = useLogto();

	const handleClick = useCallback(() => {
		// Back to this page after sign-in (an emailed Review link, say), not to home.
		// Always overwritten (home included), so an abandoned attempt's page never resurfaces.
		rememberReturnTo(currentReturnTo() ?? ROUTES.home);
		void signIn(new URL(SIGN_IN_CALLBACK_PATH, window.location.origin).toString());
	}, [signIn]);

	if (variant === "link") {
		return (
			<Link
				component="button"
				type="button"
				variant="subtitle2"
				onClick={handleClick}
				sx={{ verticalAlign: "baseline" }}>
				{t("auth.signIn")}
			</Link>
		);
	}
	return (
		<Button color={variant === "text" ? "inherit" : "primary"} variant={variant} onClick={handleClick}>
			{t("auth.signIn")}
		</Button>
	);
};
